import { WorkOrder, WorkOrderSortField, WorkOrderStatus, WorkOrderStatusType } from "../api/models";
import { useContext, useEffect, useState } from "react";
import { SubmitStatus } from "../api/work-order-api";
import ApiContext from "../api/context";
import Menu from "../views/master/menu";
import ListView from "../components/list-view";
import { Row, Tag } from "antd";
import Title from "antd/es/typography/Title";
import { toDateString, toDateTimeString, toPrintableWorkOrderStatus, toWorkOrderStatusColor } from "../constants";
import WorkOrderComponent, { WorkOrderFunction } from "../components/work-order";

interface MappedWorkOrder extends WorkOrder {
  key?: number,
  displayCreationTime: string,
  displayCurrentStatus: string,
  displayLastStatusTime: string
};

function mapWorkOrder(workOrder: WorkOrder): MappedWorkOrder {
  const displayCurrentStatus = toPrintableWorkOrderStatus(workOrder.status!.workOrderStatusType);
  const displayCreationTime = toDateString(workOrder.creationTime!);
  const displayLastStatusTime = toDateTimeString(workOrder.status?.statusTime);
  return {
    ...workOrder,
    key: workOrder.workOrderID,
    displayCreationTime,
    displayCurrentStatus,
    displayLastStatusTime
  };
}

function renderWorkOrderCard(workOrder: MappedWorkOrder) {
  return (
    <>
      <Row justify="space-between" align="middle" style={{ marginBottom: "12px" }}>
        <Title level={5} style={{ marginTop: 0, marginBottom: 0 }}>{workOrder.name}</Title>
        <Tag color={toWorkOrderStatusColor(workOrder.status!.workOrderStatusType)}>{workOrder.displayCurrentStatus}</Tag>
      </Row>
      <Row justify="space-between">
        <span>Render Count</span>
        <span>{workOrder.renderCount}</span>
      </Row>
      <Row justify="space-between">
        <span>Created</span>
        <span>{workOrder.displayCreationTime}</span>
      </Row>
      <Row justify="space-between">
        <span>Last Status</span>
        <span>{workOrder.displayLastStatusTime}</span>
      </Row>
    </>
  );
}

export default function () {
  const [workOrderStatus, setWorkOrderStatus] = useState<SubmitStatus>(SubmitStatus.Submitting);
  const [workOrders, setWorkOrders] = useState<MappedWorkOrder[]>([]);
  const [workOrder, setWorkOrder] = useState<MappedWorkOrder>();
  const [workOrderFunction, setWorkOrderFunction] = useState<WorkOrderFunction>(WorkOrderFunction.None);
  const [sortField, setSortField] = useState<WorkOrderSortField>(WorkOrderSortField.CreationTime);
  const [sortDescending, setSortDescending] = useState(false);
  const { api } = useContext(ApiContext);

  useEffect(() => {
    api?.getWorkOrders({ excludeStatuses: [WorkOrderStatusType.Archived], sortBy: sortField, sortDescending }).then(t => {
      if (!t || !t.body || t.hasError) {
        setWorkOrderStatus(SubmitStatus.Failed);
        return;
      }

      const mappedWorkOrders = t.body.map(mapWorkOrder);
      setWorkOrders(mappedWorkOrders);
      setWorkOrderStatus(SubmitStatus.Submitted);
    });
  }, [sortField, sortDescending]);

  function routeWorkOrder(routedWorkOrder: WorkOrder) {
    switch (routedWorkOrder.status?.workOrderStatusType) {
      case WorkOrderStatusType.PendingParameters:
        setWorkOrderFunction(WorkOrderFunction.UploadParameters);
        break;
      case WorkOrderStatusType.PendingDynamicAssets:
        setWorkOrderFunction(WorkOrderFunction.UploadAssets);
        break;
      case WorkOrderStatusType.QueuedForProcessing:
      case WorkOrderStatusType.QueuedForRendering:
      case WorkOrderStatusType.Rendering:
      case WorkOrderStatusType.PreparingResults:
        setWorkOrderFunction(WorkOrderFunction.ViewStatuses);
        break;
      case WorkOrderStatusType.Archived:
      case WorkOrderStatusType.Failed:
        setWorkOrderFunction(WorkOrderFunction.None);
        setWorkOrder(undefined);
        break;
      case WorkOrderStatusType.Done:
        setWorkOrderFunction(WorkOrderFunction.ViewResults);
        break;
    }
  }

  return (
    <Menu>
      <ListView
        title="Work Orders"
        storageKey="workOrdersViewMode"
        emptyText="There are no work orders to show."
        loading={workOrderStatus === SubmitStatus.Submitting}
        columns={[
          { title: "ID", dataIndex: "workOrderID", key: "workOrderID", align: "right", width: 97 },
          { title: "Name", dataIndex: "name", key: "name", align: "left" },
          { title: "Status", width: 170, dataIndex: "displayCurrentStatus", key: "displayCurrentStatus", align: "left" },
          {
            title: "Creation Date",
            width: 120,
            dataIndex: "displayCreationTime",
            key: "displayCreationTime",
            align: "right"
          }
        ]}
        rows={workOrders}
        renderCard={renderWorkOrderCard}
        sortOptions={[
          { label: "Creation Time", value: WorkOrderSortField.CreationTime },
          { label: "Name", value: WorkOrderSortField.Name },
          { label: "Last Status Time", value: WorkOrderSortField.LastStatusTime },
          { label: "Last Status", value: WorkOrderSortField.LastStatus }
        ]}
        sortField={sortField}
        onSortFieldChange={value => setSortField(value as WorkOrderSortField)}
        sortDescending={sortDescending}
        onSortDescendingChange={setSortDescending}
        onAction={() => {
          setWorkOrder(undefined);
          setWorkOrderFunction(WorkOrderFunction.Create);
        }}
        onSelect={workOrder => {
          switch (workOrder.status?.workOrderStatusType) {
            case WorkOrderStatusType.PendingParameters:
              setWorkOrder(workOrder);
              setWorkOrderFunction(WorkOrderFunction.UploadParameters);
              break;
            case WorkOrderStatusType.PendingDynamicAssets:
              setWorkOrder(workOrder);
              setWorkOrderFunction(WorkOrderFunction.UploadAssets);
              break;
            case WorkOrderStatusType.Rendering:
            case WorkOrderStatusType.QueuedForProcessing:
            case WorkOrderStatusType.QueuedForRendering:
            case WorkOrderStatusType.PreparingResults:
              setWorkOrder(workOrder);
              setWorkOrderFunction(WorkOrderFunction.ViewStatuses);
              break;
            case WorkOrderStatusType.Done:
              setWorkOrder(workOrder);
              setWorkOrderFunction(WorkOrderFunction.ViewResults);
              break;
          }
        }}
      />
      <WorkOrderComponent
        workOrderFunction={workOrderFunction}
        template={workOrder?.template}
        workOrder={workOrder}
        onClose={() => {
          setWorkOrderFunction(WorkOrderFunction.None);
          setWorkOrder(undefined);
        }}
        onCreate={createdWorkOrder => {
          const mappedWorkOrder = mapWorkOrder(createdWorkOrder);
          setWorkOrder(mappedWorkOrder);

          api?.getWorkOrders({ excludeStatuses: [WorkOrderStatusType.Archived], sortBy: sortField, sortDescending }).then(t => {
            if (!t || !t.body || t.hasError) {
              return;
            }
            setWorkOrders(t.body.map(mapWorkOrder));
          });

          routeWorkOrder(createdWorkOrder);
        }}
        onSave={savedWorkOrder => {
          const mappedWorkOrder = mapWorkOrder(savedWorkOrder);
          setWorkOrder(mappedWorkOrder);

          if (mappedWorkOrder.status?.workOrderStatusType === WorkOrderStatusType.Archived) {
            setWorkOrders(previous => previous.filter(w => w.workOrderID !== mappedWorkOrder.workOrderID));
          } else {
            setWorkOrders(previous => previous.map(w => w.workOrderID === mappedWorkOrder.workOrderID ? mappedWorkOrder : w));
          }

          routeWorkOrder(savedWorkOrder);
        }}
      />
    </Menu>
  );
}